/**
 * api.js - API client with automatic token expiry handling
 */

import auth from './Auth.js';
import BASE_URL from '../config';

const API_BASE_URL = BASE_URL;

// Callback for handling 401 errors (set by AuthContext)
let onUnauthorized = null;

/**
 * Set the callback to be called when a 401 error occurs
 * @param {Function} callback - Function to call on 401 error
 */
export const setUnauthorizedCallback = (callback) => {
  onUnauthorized = callback;
};

/**
 * Generic API request handler with automatic 401 handling
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} API response
 */
const apiRequest = async (endpoint, options = {}) => {
  try {
    const authHeaders = auth.getAuthHeaders();
    const headers = {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers
    });

    // Handle 401 Unauthorized - token expired or invalid
    if (response.status === 401) {
      auth.clearAuth();

      if (onUnauthorized) {
        onUnauthorized();
      }

      throw new Error('Your session has expired. Please sign in again.');
    }

    // Try to parse JSON response
    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      throw new Error('Unable to connect to the server. Please check your internet connection.');
    }

    if (!response.ok) {
      throw new Error(data.message || data.detail || 'Request failed. Please try again.');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Authentication API endpoints
 */
const authApi = {
  register: (companyData) => {
    return apiRequest('/register/', {
      method: 'POST',
      body: JSON.stringify(companyData)
    });
  },

  login: async (credentials) => {
    const data = await apiRequest('/login/', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });

    auth.setAuth(data);
    return data;
  },

  logout: () => {
    auth.clearAuth();
  },

  getProfile: () => {
    return apiRequest('/profile/', { method: 'GET' });
  },

  createUser: (userData) => {
    return apiRequest('/users/', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  },

  getCompanyUsers: () => {
    return apiRequest('/users/', { method: 'GET' });
  },

  updateUser: (userId, userData) => {
    return apiRequest(`/users/${userId}/`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  },

  deleteUser: (userId) => {
    return apiRequest(`/users/${userId}/`, { method: 'DELETE' });
  },

  updatePassword: (passwordData) => {
    return apiRequest('/update-password/', {
      method: 'PUT',
      body: JSON.stringify(passwordData)
    });
  },

  updateUsername: (usernameData) => {
    return apiRequest('/update-username/', {
      method: 'PUT',
      body: JSON.stringify(usernameData)
    });
  },

  getCompanyProfile: () => {
    return apiRequest('/company/', { method: 'GET' });
  },

  updateCompanyProfile: (companyData) => {
    return apiRequest('/company/', {
      method: 'PUT',
      body: JSON.stringify(companyData)
    });
  },

  updatePaymentSchedule: (scheduleData) => {
    return apiRequest('/payment-schedule/', {
      method: 'PUT',
      body: JSON.stringify(scheduleData)
    });
  },

  getApiConfigurations: () => {
    return apiRequest('/api-configurations/', { method: 'GET' });
  },

  createApiConfiguration: (configData) => {
    return apiRequest('/api-configurations/', {
      method: 'POST',
      body: JSON.stringify(configData)
    });
  },

  updateApiConfiguration: (configId, configData) => {
    return apiRequest(`/api-configurations/${configId}/`, {
      method: 'PUT',
      body: JSON.stringify(configData)
    });
  },

  deleteApiConfiguration: (configId) => {
    return apiRequest(`/api-configurations/${configId}/`, { method: 'DELETE' });
  }
};

export default {
  auth: authApi,
};